import { getAllQuestions } from '@/lib/utils/chart-utils'

/** One `_canonicalConfig.questionMap` entry the compiler writes per question. */
export interface SurveyQuestionMapEntry {
  questionId?: string
  surveyJsName?: string
}

/** The compiler's canonical question id -> SurveyJS element map, when present. */
export function readSurveyQuestionMap(
  survey: unknown,
): Record<string, SurveyQuestionMapEntry> | undefined {
  if (!survey || typeof survey !== 'object') {
    return undefined
  }
  const canonical = (survey as { _canonicalConfig?: { questionMap?: unknown } })._canonicalConfig
  const questionMap = canonical?.questionMap
  if (!questionMap || typeof questionMap !== 'object') {
    return undefined
  }
  return questionMap as Record<string, SurveyQuestionMapEntry>
}

/**
 * LEGACY READER: a survey compiled before the question map existed named each
 * SurveyJS element after its canonical question id, so the id is the name.
 * Reached only when the map has no entry for the id.
 */
function legacySurveyQuestionByName<T>(questions: Array<T>, questionId: string): T | null {
  return (
    questions.find((question) => (question as { name?: unknown }).name === questionId) ?? null
  )
}

/**
 * The compiled SurveyJS question for a canonical question id: the one report
 * resolver. Joined through the compiler's
 * `questionMap` (keyed by id, or an entry carrying the id), then by the mapped
 * `surveyJsName`; never by title. `questionMap` defaults to the survey's own.
 */
export function findSurveyQuestion<T = any>(
  survey: unknown,
  questionId: string,
  questionMap?: Record<string, SurveyQuestionMapEntry> | null,
): T | null {
  const map = questionMap ?? readSurveyQuestionMap(survey)
  const questions = getAllQuestions(survey ?? {}) as Array<T>
  const byId = map?.[questionId]
  const fromValues = map
    ? Object.values(map).find((entry) => entry.questionId === questionId)
    : undefined
  const surveyJsName = byId?.surveyJsName ?? fromValues?.surveyJsName
  if (surveyJsName) {
    const named = questions.find(
      (question) => (question as { name?: unknown }).name === surveyJsName,
    )
    if (named) {
      return named
    }
  }
  return legacySurveyQuestionByName(questions, questionId)
}
